import { Badge, Box, Grid, HStack, Icon, Link, Text, VStack } from '@chakra-ui/react'
import { EmptyMessage } from 'components/ui/emptyMessage'
import { Loading } from 'components/ui/loading'
import { NextLink } from 'components/wrapper/nextLink'
import { TopCampaignFacilityItemFragment } from 'generated/graphql'
import { formatDate } from 'helper/dateFormat'
import { FC } from 'react'
import { MdArrowCircleRight } from 'react-icons/md'
import { Routes } from 'routes'
import { buildMapSpotFallbackSearchCondition } from './buildMapSpotFallbackSearchCondition'
import { TopSectionHeader } from './topSectionHeader'
import { TopSectionLoadError } from './topSectionLoadError'

type Props = {
  campaignFacilities?: TopCampaignFacilityItemFragment[]
  loading: boolean
  error: boolean
  refetch: () => Promise<unknown>
}

export const TopCampaignFacilitySection: FC<Props> = ({ campaignFacilities, loading, error, refetch }) => (
  <Box as="section">
    <TopSectionHeader title="キャンペーン施設" description="HoteCoキャンペーン施設をご紹介しています" />

    <Box mt="24px">
      {!campaignFacilities && loading ? (
        <Loading />
      ) : !campaignFacilities && error ? (
        <TopSectionLoadError resourceName="キャンペーン施設" onRetry={refetch} />
      ) : !campaignFacilities || campaignFacilities.length === 0 ? (
        <EmptyMessage>現在ご案内できるキャンペーン施設はありません</EmptyMessage>
      ) : (
        <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="16px">
          {campaignFacilities.map(facility => {
            const href = { pathname: Routes.searchHotelsMap(), query: buildMapSpotFallbackSearchCondition(facility) }
            return (
              <Box key={facility.id} p="24px" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="8px" boxShadow="sm">
                <HStack spacing="8px" align="center">
                  <Badge variant="outline" borderColor="gray.800" color="gray.800" borderRadius="4px" px="8px" py="2px">
                    {facility.minStayText}
                  </Badge>
                  <Text color="primary" fontSize="sm" fontWeight="medium">
                    {facility.cityName}
                  </Text>
                </HStack>

                <NextLink href={href} passHref legacyBehavior>
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-block"
                    mt="8px"
                    color="gray.800"
                    fontSize="18px"
                    fontWeight="bold"
                  >
                    {facility.title}
                  </Link>
                </NextLink>

                <VStack mt="16px" spacing="8px" align="stretch" fontSize="sm">
                  <Box>
                    <Text color="gray.500">宿泊可能期間</Text>
                    <Text wordBreak="break-word">{facility.stayPeriodText}</Text>
                  </Box>
                  <Box>
                    <Text color="gray.500">予約可能期間</Text>
                    <Text wordBreak="break-word">{facility.bookingPeriodText}</Text>
                  </Box>
                </VStack>

                <HStack mt="20px" justify="space-between" align="center">
                  <Text color="gray.500" fontSize="xs">
                    登録日 {formatDate(new Date(facility.createdAt), 'yyyy/MM/dd')}
                  </Text>
                  <NextLink href={href} passHref legacyBehavior>
                    <Link target="_blank" rel="noopener noreferrer" color="blue.600" fontSize="sm" fontWeight="medium">
                      <HStack spacing="2px">
                        <Icon as={MdArrowCircleRight} boxSize="18px" />
                        <Text>詳細を見る</Text>
                      </HStack>
                    </Link>
                  </NextLink>
                </HStack>
              </Box>
            )
          })}
        </Grid>
      )}
    </Box>
  </Box>
)
